import React, { ReactNode, useState, useContext } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  Button,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CssBaseline,
  useTheme,
  Menu,
  MenuItem,
  Avatar,
  alpha,
  Collapse,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import {
  Dashboard as DashboardIcon,
  Tv as TvIcon,
  Campaign as CampaignIcon,
  VideoLibrary as VideoLibraryIcon,
  AccountCircle as AccountCircleIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Assignment as AssignmentIcon,
  Assessment as AssessmentIcon,
  Bookmark as BookmarkIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  Inventory as InventoryIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  SettingsApplications as SettingsIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  GraphicEq as GraphicEqIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
}  from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ThemeContext } from '../../theme/ThemeContext';

// Configuração do layout
const drawerWidth = 260;

// Atualizar a interface do User para incluir is_admin
interface User {
  id: number;
  nome: string;
  email: string;
  roles: string[];
  is_admin: boolean;
}

// Tipagem para itens de menu
interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path?: string;
  type?: string;
  id?: string;
  items?: MenuItem[];
}

interface SidebarLayoutProps {
  children: ReactNode;
}

const SidebarLayout: React.FC<SidebarLayoutProps> = ({ children }: SidebarLayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user: authUser } = useAuth();
  const user = authUser as User;
  const theme = useTheme();
  const { mode, toggleColorMode } = useContext(ThemeContext);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Verificar se o usuário tem permissão de admin
  const isAdmin = user?.is_admin === true;
  
  // Se não for admin e tentar acessar página restrita, redirecionar para dashboard ou relatórios
  React.useEffect(() => {
    const allowedNonAdminPaths = ['/relatorios', '/login', '/dashboard'];
    if (!isAdmin && !allowedNonAdminPaths.includes(location.pathname)) {
      navigate('/dashboard');
    }
  }, [isAdmin, location.pathname, navigate]);

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogoutAndClose = () => {
    handleMenuClose();
    logout();
    navigate('/login');
  };

  // Estado para controlar o colapso dos grupos de menu
  const [openMenuGroups, setOpenMenuGroups] = useState<Record<string, boolean>>({
    tipagens: false, // Começa fechado por padrão
  });

  const toggleMenuGroup = (groupId: string) => {
    setOpenMenuGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  // Itens do menu principal
  // Organize os itens do menu em duas categorias: admin e usuário comum
  const adminMenuItems: MenuItem[] = [
    {
      text: 'Registros',
      icon: <AssignmentIcon />,
      path: '/registros',
    },
    {
      text: 'Emissoras',
      icon: <TvIcon />,
      path: '/emissoras',
    },
    {
      text: 'Clientes',
      icon: <PeopleIcon />,
      path: '/clientes',
    },
    {
      text: 'Anúncios',
      icon: <CampaignIcon />,
      path: '/anuncios',
    },
    {
      text: 'Gravações',
      icon: <VideoLibraryIcon />,
      path: '/gravacoes',
    },
    {
      text: 'Usuários',
      icon: <PersonIcon />,
      path: '/users',
    },
    {
      type: 'group',
      id: 'tipagens',
      text: 'Tipagens',
      icon: <SettingsIcon />,
      items: [
        {
          text: 'Agências',
          icon: <BusinessIcon />,
          path: '/agencias',
        },
        {
          text: 'Anunciantes',
          icon: <BusinessIcon />,
          path: '/anunciantes',
        },
        {
          text: 'Categorias',
          icon: <CategoryIcon />,
          path: '/categorias',
        },
        {
          text: 'Itens',
          icon: <InventoryIcon />,
          path: '/itens',
        },
        {
          text: 'Programas',
          icon: <TvIcon />,
          path: '/programas',
        },
        {
          text: 'Marcas',
          icon: <BookmarkIcon />,
          path: '/marcas',
        },
        {
          text: 'Setores',
          icon: <CategoryIcon />,
          path: '/setores',
        },
      ]
    },
  ];
  
  const userMenuItems: MenuItem[] = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
    },
    {
      text: 'Relatórios',
      icon: <AssessmentIcon />,
      path: '/relatorios',
    },
  ];
  
  // Item do Dashboard que deve aparecer no topo para todos os usuários
  const dashboardItem: MenuItem = {
    text: 'Dashboard',
    icon: <DashboardIcon />,
    path: '/dashboard',
  };

  // Remover Dashboard do userMenuItems para não ter duplicação
  const filteredUserMenuItems = userMenuItems.filter(item => item.path !== '/dashboard');
  
  // Determinar quais itens do menu mostrar com base no papel do usuário
  const menuItems = isAdmin 
    ? [dashboardItem, ...adminMenuItems, ...filteredUserMenuItems] 
    : [dashboardItem, ...filteredUserMenuItems];

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      {/* Barra superior */}
      <AppBar 
        position="fixed"
        elevation={0}
        sx={{ 
          width: `calc(100% - ${drawerWidth}px)`,
          ml: `${drawerWidth}px`,
          zIndex: (theme: Theme) => theme.zIndex.drawer + 1,
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar>
          <Box sx={{ flexGrow: 1 }} />
          {/* Dark mode toggle button */}
          <Box 
            sx={{ 
              mr: 2, 
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Button
              onClick={toggleColorMode}
              sx={{
                minWidth: 'auto',
                p: 1,
                borderRadius: '50%',
                color: theme.palette.text.primary
              }}
              aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </Button>
          </Box>
          {user && (
            <>
              <Button
                onClick={handleMenuOpen}
                aria-controls="menu-appbar"
                aria-haspopup="true"
                endIcon={<KeyboardArrowDownIcon />}
                sx={{ 
                  textTransform: 'none', 
                  borderRadius: theme.shape.borderRadius,
                  color: theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  },
                }}
              >
                <Avatar 
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                    color: theme.palette.primary.main,
                    fontSize: 16,
                    fontWeight: 'bold',
                    mr: 1
                  }}
                >
                  {user.nome ? user.nome.charAt(0).toUpperCase() : user.email?.charAt(0).toUpperCase()}
                </Avatar>
                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                  {user.nome || user.email}
                </Typography>
              </Button>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                  elevation: 2,
                  sx: { mt: 1, minWidth: 180 }
                }}
              >
                <MenuItem disabled sx={{ '&.Mui-disabled': { opacity: 1, color: theme.palette.text.primary } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                    Olá, {user.nome || user.email}
                  </Typography>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogoutAndClose}>Sair</MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>
      
      {/* Barra lateral */}
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: theme.palette.primary.dark,
            color: 'white',
            borderRight: 'none',
          },
        }}
        variant="permanent"
        anchor="left"
      >
        <Toolbar
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            px: [2],
            height: 64,
            bgcolor: theme.palette.primary.dark,
          }}
        >
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <GraphicEqIcon 
              sx={{ 
                fontSize: 30, 
                color: 'white', 
                mr: 1 
              }} 
            />
          </Box>
        </Toolbar>
        <Divider sx={{ borderColor: alpha('#ffffff', 0.12) }} />
        <List sx={{ p: 2 }}>
          {menuItems.map((item, index) => {
            // Para grupos de menu
            if (item.type === 'group') {
              const groupItem = item as {
                id: string;
                type: string;
                text: string;
                icon: React.ReactNode;
                items: {
                  text: string;
                  icon: React.ReactNode;
                  path: string;
                }[];
              };
              
              const isOpen = openMenuGroups[groupItem.id] || false;
              const hasActiveChild = groupItem.items.some(
                subItem => location.pathname === subItem.path
              );
              
              return (
                <React.Fragment key={`group-${groupItem.id}`}>
                  <ListItem 
                    disablePadding 
                    sx={{ mb: 0.5 }}
                  >
                    <ListItemButton 
                      onClick={() => toggleMenuGroup(groupItem.id)}
                      sx={{
                        transition: theme => 
                          theme.transitions.create(['background-color', 'box-shadow'], {
                            duration: theme.transitions.duration.short,
                            easing: theme.transitions.easing.easeInOut,
                          }),
                        borderRadius: theme.shape.borderRadius,
                        backgroundColor: hasActiveChild
                          ? alpha('#ffffff', 0.12) 
                          : 'transparent',
                        '&:hover': {
                          backgroundColor: hasActiveChild
                            ? alpha('#ffffff', 0.18)
                            : alpha('#ffffff', 0.08),
                        },
                        py: 1.2,
                      }}
                    >
                      <ListItemIcon 
                        sx={{ 
                          minWidth: 'auto', 
                          mr: 2,
                          color: 'white',
                          opacity: hasActiveChild ? 1 : 0.8,
                        }}
                      >
                        {groupItem.icon}
                      </ListItemIcon>
                      <ListItemText 
                        primary={groupItem.text} 
                        primaryTypographyProps={{ 
                          variant: 'body2', 
                          fontWeight: hasActiveChild ? 600 : 500,
                          color: 'white',
                          sx: { opacity: hasActiveChild ? 1 : 0.8 }
                        }}
                      />
                      {isOpen ? 
                        <ExpandLessIcon 
                          sx={{ 
                            color: 'white',
                            transition: theme => 
                              theme.transitions.create(['transform', 'opacity'], {
                                duration: theme.transitions.duration.shorter,
                                easing: theme.transitions.easing.easeInOut,
                              }),
                            transform: 'rotate(0deg)',
                          }} 
                        /> : 
                        <ExpandMoreIcon 
                          sx={{ 
                            color: 'white',
                            transition: theme => 
                              theme.transitions.create(['transform', 'opacity'], {
                                duration: theme.transitions.duration.shorter,
                                easing: theme.transitions.easing.easeInOut,
                              }),
                            transform: 'rotate(0deg)',
                          }} 
                        />}
                    </ListItemButton>
                  </ListItem>
                  
                  <Collapse 
                    in={isOpen} 
                    timeout={400} 
                    easing="cubic-bezier(0.4, 0, 0.2, 1)"
                    unmountOnExit
                    sx={{
                      transition: theme => 
                        theme.transitions.create(['height', 'opacity', 'transform'], {
                          duration: theme.transitions.duration.standard,
                          easing: theme.transitions.easing.easeInOut,
                        }),
                      transformOrigin: 'top',
                      opacity: isOpen ? 1 : 0,
                    }}
                  >
                    <List component="div" disablePadding sx={{ pl: 2 }}>
                      {groupItem.items.map((subItem) => {
                        const isActive = location.pathname === subItem.path;
                        return (
                          <ListItem 
                            key={subItem.text} 
                            disablePadding 
                            sx={{ mb: 0.5 }}
                          >
                            <ListItemButton 
                              onClick={() => handleNavigation(subItem.path)}
                              sx={{
                                borderRadius: theme.shape.borderRadius,
                                backgroundColor: isActive 
                                  ? alpha('#ffffff', 0.12) 
                                  : 'transparent',
                                '&:hover': {
                                  backgroundColor: isActive 
                                    ? alpha('#ffffff', 0.18)
                                    : alpha('#ffffff', 0.08),
                                },
                                py: 1,
                              }}
                            >
                              <ListItemIcon 
                                sx={{ 
                                  minWidth: 'auto', 
                                  mr: 2,
                                  color: 'white',
                                  opacity: isActive ? 1 : 0.8,
                                }}
                              >
                                {subItem.icon}
                              </ListItemIcon>
                              <ListItemText 
                                primary={subItem.text} 
                                primaryTypographyProps={{ 
                                  variant: 'body2', 
                                  fontWeight: isActive ? 600 : 500,
                                  color: 'white',
                                  sx: { opacity: isActive ? 1 : 0.8 }
                                }}
                              />
                              {isActive && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    right: 0,
                                    width: 4,
                                    height: '60%',
                                    backgroundColor: 'white',
                                    borderRadius: '4px 0 0 4px',
                                  }}
                                />
                              )}
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Collapse>
                </React.Fragment>
              );
            }
            
            // Para itens de menu normais
            const normalItem = item as {
              text: string;
              icon: React.ReactNode;
              path: string;
            };
            
            const isActive = location.pathname === normalItem.path;
            return (
              <ListItem 
                key={normalItem.text} 
                disablePadding 
                sx={{ mb: 1 }}
              >
                <ListItemButton 
                  onClick={() => handleNavigation(normalItem.path)}
                  sx={{
                    borderRadius: theme.shape.borderRadius,
                    backgroundColor: isActive 
                      ? alpha('#ffffff', 0.12) 
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: isActive 
                        ? alpha('#ffffff', 0.18)
                        : alpha('#ffffff', 0.08),
                    },
                    py: 1.2,
                  }}
                >
                  <ListItemIcon 
                    sx={{ 
                      minWidth: 'auto', 
                      mr: 2,
                      color: 'white',
                      opacity: isActive ? 1 : 0.8,
                    }}
                  >
                    {normalItem.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={normalItem.text} 
                    primaryTypographyProps={{ 
                      variant: 'body2', 
                      fontWeight: isActive ? 600 : 500,
                      color: 'white',
                      sx: { opacity: isActive ? 1 : 0.8 }
                    }}
                  />
                  {isActive && (
                    <Box
                      sx={{
                        position: 'absolute',
                        right: 0,
                        width: 4,
                        height: '60%',
                        backgroundColor: 'white',
                        borderRadius: '4px 0 0 4px',
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Drawer>
      
      {/* Conteúdo principal */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { xs: '100%', sm: `calc(100% - ${drawerWidth}px)` },
          maxWidth: { sm: '100%', md: '1800px' },
          mx: 'auto',
          mt: '64px',
          bgcolor: theme.palette.background.default,
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default SidebarLayout; 